/**
 * SVG движок для визуализации CSElement структур
 */

import {
  VisualizationEngine,
  VisualizationGraph,
  VisualizationOptions,
  VisualizationResult,
  VisualizationFormat,
  VisualizationNode,
  VisualizationEdge,
  NodeStyle,
  EdgeStyle,
  LayoutType
} from '../../types/visualization-interfaces';

export class SvgEngine implements VisualizationEngine {
  name = 'svg';
  supportedFormats: VisualizationFormat[] = ['svg'];

  /**
   * Проверка поддержки формата
   */
  supportsFormat(format: VisualizationFormat): boolean {
    return this.supportedFormats.includes(format);
  }

  /**
   * Рендеринг графа в SVG формат
   */
  async render(
    graph: VisualizationGraph,
    options: VisualizationOptions
  ): Promise<VisualizationResult> {
    const startTime = Date.now();
    
    const {
      width = 800,
      height = 600,
      layout = 'tree',
      nodeStyle = {},
      edgeStyle = {},
      title,
      interactive = false,
      nodeSpacing = 100,
      levelSpacing = 80
    } = options;

    // Вычисляем позиции узлов
    const positionedNodes = this.calculatePositions(graph, layout, {
      width,
      height,
      nodeSpacing,
      levelSpacing
    });

    // Генерируем SVG
    const svgContent = this.generateSvg(
      positionedNodes,
      graph.edges,
      {
        width,
        height,
        nodeStyle,
        edgeStyle,
        title,
        interactive
      }
    );

    return {
      content: svgContent,
      format: 'svg',
      metadata: {
        nodeCount: graph.nodes.length,
        edgeCount: graph.edges.length,
        maxDepth: graph.metadata.maxDepth,
        generationTime: Date.now() - startTime,
        dimensions: { width, height }
      }
    };
  }

  /**
   * Вычисление позиций узлов
   */
  private calculatePositions(
    graph: VisualizationGraph,
    layout: LayoutType,
    dimensions: {
      width: number;
      height: number;
      nodeSpacing: number;
      levelSpacing: number;
    }
  ): VisualizationNode[] {
    switch (layout) {
      case 'tree':
        return this.calculateTreeLayout(graph, dimensions);
      case 'circular':
        return this.calculateCircularLayout(graph, dimensions);
      case 'force':
        return this.calculateForceLayout(graph, dimensions);
      default:
        return this.calculateTreeLayout(graph, dimensions);
    }
  }

  /**
   * Древовидная раскладка
   */
  private calculateTreeLayout(
    graph: VisualizationGraph,
    dimensions: {
      width: number;
      height: number;
      nodeSpacing: number;
      levelSpacing: number;
    }
  ): VisualizationNode[] {
    const { width, height } = dimensions;
    const nodes = [...graph.nodes];

    // Группируем узлы по уровням
    const levelGroups = new Map<number, VisualizationNode[]>();
    nodes.forEach(node => {
      const level = node.level;
      if (!levelGroups.has(level)) {
        levelGroups.set(level, []);
      }
      levelGroups.get(level)!.push(node);
    });

    // Вычисляем позиции
    const maxLevel = Math.max(...Array.from(levelGroups.keys()));
    
    levelGroups.forEach((levelNodes, level) => {
      const y = (height / (maxLevel + 1)) * (level + 1);
      const nodeWidth = width / (levelNodes.length + 1);
      
      levelNodes.forEach((node, index) => {
        node.x = nodeWidth * (index + 1);
        node.y = y;
        node.width = 120;
        node.height = 40;
      });
    });

    return nodes;
  }

  /**
   * Круговая раскладка
   */
  private calculateCircularLayout(
    graph: VisualizationGraph,
    dimensions: { width: number; height: number }
  ): VisualizationNode[] {
    const { width, height } = dimensions;
    const nodes = [...graph.nodes];
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) * 0.3;

    nodes.forEach((node, index) => {
      const angle = (2 * Math.PI * index) / nodes.length;
      node.x = centerX + radius * Math.cos(angle);
      node.y = centerY + radius * Math.sin(angle);
      node.width = 100;
      node.height = 30;
    });

    return nodes;
  }

  /**
   * Силовая раскладка (упрощенная)
   */
  private calculateForceLayout(
    graph: VisualizationGraph,
    dimensions: { width: number; height: number }
  ): VisualizationNode[] {
    const { width, height } = dimensions;
    const nodes = [...graph.nodes];

    // Инициализируем случайные позиции
    nodes.forEach(node => {
      node.x = Math.random() * width;
      node.y = Math.random() * height;
      node.width = 100;
      node.height = 30;
    });

    // Простая симуляция сил (несколько итераций)
    for (let i = 0; i < 50; i++) {
      this.applyForces(nodes, graph.edges, dimensions);
    }

    return nodes;
  }

  /**
   * Применение сил для силовой раскладки
   */
  private applyForces(
    nodes: VisualizationNode[],
    edges: VisualizationEdge[],
    dimensions: { width: number; height: number }
  ): void {
    const { width, height } = dimensions;
    const repulsionStrength = 1000;
    const attractionStrength = 0.1;
    const damping = 0.9;

    // Инициализируем силы
    const forces = new Map<string, { fx: number; fy: number }>();
    nodes.forEach(node => {
      forces.set(node.id, { fx: 0, fy: 0 });
    });

    // Силы отталкивания между всеми узлами
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const node1 = nodes[i];
        const node2 = nodes[j];
        
        const dx = node1.x! - node2.x!;
        const dy = node1.y! - node2.y!;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;
        
        const force = repulsionStrength / (distance * distance);
        const fx = (dx / distance) * force;
        const fy = (dy / distance) * force;
        
        const force1 = forces.get(node1.id)!;
        const force2 = forces.get(node2.id)!;
        
        force1.fx += fx;
        force1.fy += fy;
        force2.fx -= fx;
        force2.fy -= fy;
      }
    }

    // Силы притяжения для связанных узлов
    edges.forEach(edge => {
      const sourceNode = nodes.find(n => n.id === edge.source);
      const targetNode = nodes.find(n => n.id === edge.target);
      
      if (sourceNode && targetNode) {
        const dx = targetNode.x! - sourceNode.x!;
        const dy = targetNode.y! - sourceNode.y!;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;
        
        const force = distance * attractionStrength;
        const fx = (dx / distance) * force;
        const fy = (dy / distance) * force;
        
        const sourceForce = forces.get(sourceNode.id)!;
        const targetForce = forces.get(targetNode.id)!;
        
        sourceForce.fx += fx;
        sourceForce.fy += fy;
        targetForce.fx -= fx;
        targetForce.fy -= fy;
      }
    });

    // Применяем силы к позициям
    nodes.forEach(node => {
      const force = forces.get(node.id)!;
      
      node.x = Math.max(50, Math.min(width - 50, node.x! + force.fx * damping));
      node.y = Math.max(30, Math.min(height - 30, node.y! + force.fy * damping));
    });
  }

  /**
   * Генерация SVG контента
   */
  private generateSvg(
    nodes: VisualizationNode[],
    edges: VisualizationEdge[],
    options: {
      width: number;
      height: number;
      nodeStyle: NodeStyle;
      edgeStyle: EdgeStyle;
      title?: string;
      interactive: boolean;
    }
  ): string {
    const { width, height, nodeStyle, edgeStyle, title, interactive } = options;

    const svgParts: string[] = [];
    
    // SVG заголовок
    svgParts.push(
      `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">`
    );

    // Стили
    if (interactive) {
      svgParts.push(`
        <style>
          .node { cursor: pointer; transition: all 0.2s; }
          .node:hover { opacity: 0.8; transform: scale(1.05); }
          .edge { transition: stroke-width 0.2s; }
          .node:hover + .edge { stroke-width: 3; }
        </style>
      `);
    }

    // Заголовок
    if (title) {
      svgParts.push(`
        <text x="${width / 2}" y="30" text-anchor="middle" 
              font-family="Arial, sans-serif" font-size="18" font-weight="bold">
          ${this.escapeXml(title)}
        </text>
      `);
    }

    // Группа для содержимого
    svgParts.push('<g>');

    // Рендерим связи
    edges.forEach(edge => {
      const sourceNode = nodes.find(n => n.id === edge.source);
      const targetNode = nodes.find(n => n.id === edge.target);
      
      if (sourceNode && targetNode) {
        svgParts.push(this.renderEdge(sourceNode, targetNode, edge, edgeStyle));
      }
    });

    // Рендерим узлы
    nodes.forEach(node => {
      svgParts.push(this.renderNode(node, nodeStyle, interactive));
    });

    svgParts.push('</g>');
    svgParts.push('</svg>');

    return svgParts.join('\n');
  }

  /**
   * Рендеринг узла
   */
  private renderNode(
    node: VisualizationNode,
    style: NodeStyle,
    interactive: boolean
  ): string {
    const {
      backgroundColor = '#f8fafc',
      borderColor = '#d1d5db',
      borderWidth = 1,
      textColor = '#1f2937',
      fontSize = 12,
      shape = 'rectangle',
      borderRadius = 4
    } = style;

    const x = node.x! - (node.width! / 2);
    const y = node.y! - (node.height! / 2);
    const className = interactive ? 'node' : '';

    let shapeElement = '';
    
    if (shape === 'circle') {
      const radius = Math.min(node.width!, node.height!) / 2;
      shapeElement = `
        <circle cx="${node.x}" cy="${node.y}" r="${radius}"
                fill="${backgroundColor}" stroke="${borderColor}" 
                stroke-width="${borderWidth}" class="${className}" />
      `;
    } else {
      shapeElement = `
        <rect x="${x}" y="${y}" width="${node.width}" height="${node.height}"
              fill="${backgroundColor}" stroke="${borderColor}" 
              stroke-width="${borderWidth}" rx="${borderRadius}" 
              class="${className}" />
      `;
    }

         const textElement = `
       <text x="${node.x}" y="${node.y! + fontSize / 3}" text-anchor="middle"
             font-family="Arial, sans-serif" font-size="${fontSize}" 
             fill="${textColor}" pointer-events="none">
         ${this.escapeXml(this.truncateText(node.label, 15))}
       </text>
     `;

    return shapeElement + textElement;
  }

  /**
   * Рендеринг связи
   */
  private renderEdge(
    sourceNode: VisualizationNode,
    targetNode: VisualizationNode,
    _edge: VisualizationEdge,
    style: EdgeStyle
  ): string {
    const {
      color = '#6b7280',
      width = 1,
      style: lineStyle = 'solid',
      arrowType = 'arrow'
    } = style;

    const strokeDasharray = lineStyle === 'dashed' ? '5,5' : 
                           lineStyle === 'dotted' ? '2,2' : 'none';

    // Вычисляем точки соединения (края узлов)
    const { x1, y1, x2, y2 } = this.calculateConnectionPoints(sourceNode, targetNode);

    let pathElement = `
      <line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}"
            stroke="${color}" stroke-width="${width}" 
            stroke-dasharray="${strokeDasharray}" class="edge" />
    `;

    // Добавляем стрелку
    if (arrowType === 'arrow') {
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const arrowLength = 10;
      const arrowAngle = Math.PI / 6;

      const arrowX1 = x2 - arrowLength * Math.cos(angle - arrowAngle);
      const arrowY1 = y2 - arrowLength * Math.sin(angle - arrowAngle);
      const arrowX2 = x2 - arrowLength * Math.cos(angle + arrowAngle);
      const arrowY2 = y2 - arrowLength * Math.sin(angle + arrowAngle);

      pathElement += `
        <polygon points="${x2},${y2} ${arrowX1},${arrowY1} ${arrowX2},${arrowY2}"
                 fill="${color}" />
      `;
    }

    return pathElement;
  }

  /**
   * Вычисление точек соединения между узлами
   */
  private calculateConnectionPoints(
    sourceNode: VisualizationNode,
    targetNode: VisualizationNode
  ): { x1: number; y1: number; x2: number; y2: number } {
    const dx = targetNode.x! - sourceNode.x!;
    const dy = targetNode.y! - sourceNode.y!;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Нормализованный вектор направления
    const unitX = dx / distance;
    const unitY = dy / distance;

    // Отступы от краев узлов
    const sourceRadius = Math.max(sourceNode.width!, sourceNode.height!) / 2;
    const targetRadius = Math.max(targetNode.width!, targetNode.height!) / 2;

    return {
      x1: sourceNode.x! + unitX * sourceRadius,
      y1: sourceNode.y! + unitY * sourceRadius,
      x2: targetNode.x! - unitX * targetRadius,
      y2: targetNode.y! - unitY * targetRadius
    };
  }

  /**
   * Обрезка текста
   */
  private truncateText(text: string, maxLength: number): string {
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }

  /**
   * Экранирование XML символов
   */
  private escapeXml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
} 